// src/config/coordinateurs.config.ts — SCE Issues
// Mapping métier → coordinateurs référents
//
// Depuis le 22/10, les listes se gèrent dans le panneau d'administration
// (bloc `roles` de la ligne __global__, config/registreRoles) ; celles d'ici
// sont les valeurs PAR DÉFAUT, tant que rien n'y a été enregistré.

import type { Metier } from '@/types/issue.types'
import { rolesCharges } from './registreRoles'

export interface Coordinateur {
  nom: string                       // Nom affiché
  email: string                     // Email AD
  upn: string                       // userPrincipalName AD
}

// ─── Coordinateurs réels (référentiel unique, pas de duplication) ───

const THIBAUT_HACQUIN: Coordinateur = {
  nom: 'Thibaut Hacquin',
  email: 'thibaut.hacquin@sartorius.com',
  upn: 'thibaut.hacquin@sartorius.com',
}

const ROMUALD_RUSE: Coordinateur = {
  nom: 'Romuald Ruse',
  email: 'romuald.ruse@sartorius.com',
  upn: 'romuald.ruse@sartorius.com',
}

const SEBASTIEN_VAUTELIN: Coordinateur = {
  nom: 'Sébastien Vautelin',
  email: 'sebastien.vautelin@sartorius.com',
  upn: 'sebastien.vautelin@sartorius.com',
}

/**
 * Coordinateurs de référence par métier.
 *
 * Plusieurs coordinateurs possibles par métier — tous sont assignés sur la
 * fiche du problème (champ SharePoint Coordinateur en multi-valeurs) et tous
 * reçoivent les notifications Power Automate.
 *
 * Un même coordinateur peut couvrir plusieurs métiers (cf. Sébastien Vautelin
 * sur MECA et PROCESS).
 */
export const COORDINATEURS_DEFAUT: Record<Metier, Coordinateur[]> = {
  ELEC: [THIBAUT_HACQUIN],
  AUTOM: [THIBAUT_HACQUIN],
  MECA: [ROMUALD_RUSE, SEBASTIEN_VAUTELIN],
  PROCESS: [SEBASTIEN_VAUTELIN],
}

// ─── Suppléants (Lot 10, 25/09) ───

const HUGO_PEULTIER: Coordinateur = {
  nom: 'Hugo Peultier',
  email: 'hugo.peultier@sartorius.com',
  upn: 'hugo.peultier@sartorius.com',
}

/**
 * Suppléants par métier : mêmes droits et même accueil que le coordinateur
 * (qualifier, annuler, clore, modifier, couleur du métier), mais PAS inscrits
 * dans la colonne Coordinateur des fiches — le titulaire reste le contact.
 * Power Automate les met en copie du titulaire (réglé dans les flows).
 *
 * D'où deux familles de fonctions ci-dessous :
 *  - `getCoordinateurs` → titulaires seuls : ce qu'on ÉCRIT sur la fiche et
 *    ce qu'on AFFICHE comme « qui doit qualifier » ;
 *  - `isCoordinateur`, `getMetiersCoordonnes`, `pilote` → titulaires ET
 *    suppléants : les DROITS et les vues de travail.
 */
export const SUPPLEANTS_DEFAUT: Record<Metier, Coordinateur[]> = {
  ELEC: [HUGO_PEULTIER],
  AUTOM: [HUGO_PEULTIER],
  MECA: [],
  PROCESS: [],
}

// ─── Mode test des notifications ───
//
// Il vit DANS chaque flow Power Automate (variables MODE_TEST et UPN_TEST),
// pas ici : les constantes MODE_TEST_NOTIFICATIONS / UPN_TEST_REDIRECTION
// n'étaient lues par personne et ont été retirées au Lot 15.
// Les flows 1 et 3 lisent la même liste (bloc `roles` de __global__) depuis le
// 22/10 : cf. docs/flows/LISEZMOI.md § « Coordinateurs ».

// ─── Les listes en vigueur : celles du panneau d'administration, sinon celles d'ici ───

const enCoordinateur = (p: { nom: string; upn: string }): Coordinateur => ({ nom: p.nom, email: p.upn, upn: p.upn })

/** Titulaires de chaque métier. */
export function coordinateursParMetier(): Record<Metier, Coordinateur[]> {
  const r = rolesCharges()
  if (!r) return COORDINATEURS_DEFAUT
  return {
    ELEC: r.coordinateurs.ELEC.titulaires.map(enCoordinateur),
    AUTOM: r.coordinateurs.AUTOM.titulaires.map(enCoordinateur),
    MECA: r.coordinateurs.MECA.titulaires.map(enCoordinateur),
    PROCESS: r.coordinateurs.PROCESS.titulaires.map(enCoordinateur),
  }
}

/** Suppléants de chaque métier. */
export function suppleantsParMetier(): Record<Metier, Coordinateur[]> {
  const r = rolesCharges()
  if (!r) return SUPPLEANTS_DEFAUT
  return {
    ELEC: r.coordinateurs.ELEC.suppleants.map(enCoordinateur),
    AUTOM: r.coordinateurs.AUTOM.suppleants.map(enCoordinateur),
    MECA: r.coordinateurs.MECA.suppleants.map(enCoordinateur),
    PROCESS: r.coordinateurs.PROCESS.suppleants.map(enCoordinateur),
  }
}

// ─── Helpers ───

/**
 * Récupère les coordinateurs TITULAIRES d'un métier donné — ceux qu'on écrit
 * dans la colonne Coordinateur. Sans les suppléants, volontairement.
 */
export function getCoordinateurs(metier: Metier): Coordinateur[] {
  return coordinateursParMetier()[metier]
}

/** Titulaires puis suppléants d'un métier : qui a le droit de le piloter. */
export function pilotesDuMetier(metier: Metier): Coordinateur[] {
  return [...coordinateursParMetier()[metier], ...suppleantsParMetier()[metier]]
}

const METIERS: readonly Metier[] = ['ELEC', 'AUTOM', 'MECA', 'PROCESS']

/**
 * Retourne la liste des métiers pilotés par un UPN donné, comme titulaire ou
 * comme suppléant. Vide si l'utilisateur n'est ni l'un ni l'autre.
 *
 * Utile pour filtrer un dashboard "mes problèmes" ou adapter l'UI selon
 * les métiers que pilote l'utilisateur connecté.
 * Comparaison case-insensitive — Azure AD ne garantit pas la casse stable.
 */
export function getMetiersCoordonnes(upn: string): Metier[] {
  if (!upn) return []
  const target = upn.toLowerCase()
  return METIERS.filter((metier) =>
    pilotesDuMetier(metier).some((c) => c.upn.toLowerCase() === target),
  )
}

/**
 * Vérifie si un UPN pilote au moins un métier (titulaire ou suppléant).
 * Utilisé par useCurrentUser pour détecter le rôle.
 */
export function isCoordinateur(upn: string): boolean {
  return getMetiersCoordonnes(upn).length > 0
}

/** L'UPN pilote-t-il CE métier (titulaire ou suppléant) ? */
export function pilote(upn: string, metier: Metier): boolean {
  return getMetiersCoordonnes(upn).includes(metier)
}

